import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import { getIsEnabledFlightsLuxPlusPricing, getIsLuxPlusEnabled } from 'luxPlus/selectors/featureToggle'
import { LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN } from 'luxPlus/constants/base'
import { isLeLivePromotionGeoState } from 'constants/config/region'
import { dateIsBefore, dateIsAfterOrSame } from 'lib/datetime/dateUtils'
import { isBannerDismissed, recordSiteBannerDismissal } from 'storage/siteBanner'
import { useIsBannerHidden } from 'hooks/useIsBannerHidden'
import { isAgentHubEnabled } from 'agentHub/selectors/isAgentHubEnabled'
import ModalContext from 'contexts/ModalContext'
import LuxPlusFlightVoucherModal from 'luxPlus/components/modals/LuxPlusFlightVoucherModal'
import useSwitchRegion from 'hooks/useSwitchRegion'

const BANNER_ID = 'luxPlusFlightVoucherOfferBanner'
const FLIGHT_VOUCHER_LANDING_PAGE_PATH = '/lux-plus?benefit=flight-voucher'

function useLuxPlusFlightVoucherBanner() {
  const currentRegionCode = useAppSelector(state => state.geo.currentRegionCode)
  const geoStateName = useAppSelector(state => state.geo.geoStateName)
  const currentRegionCodeLower = currentRegionCode.toLowerCase()
  const memberSubscriptionItem = useAppSelector(state => state.auth.account.luxPlus.member.subscription.item)
  const isEnabledFlightsLuxPlusPricing = useAppSelector(getIsEnabledFlightsLuxPlusPricing)
  const isLuxPlusEnabled = useAppSelector(getIsLuxPlusEnabled)
  const disableMembership = useAppSelector(state => state.auth.account.luxPlus.member.disableMembership)
  const isSpoofed = useAppSelector(state => state.auth.account.isSpoofed)
  const isHidden = useIsBannerHidden(BANNER_ID)

  const now = new Date()
  const isCampaignActive = dateIsAfterOrSame(now, LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.START_DATE) &&
    dateIsBefore(now, LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE)

  const [isDismissed, setIsDismissed] = useState(false)

  useEffect(() => {
    setIsDismissed(isBannerDismissed(BANNER_ID))
  }, [])

  const onDismiss = useCallback(() => {
    setIsDismissed(true)
    recordSiteBannerDismissal(BANNER_ID, LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.END_DATE.toISOString())
  }, [])

  const intl = useIntl()
  const showModal = useContext(ModalContext)
  const memberRegion = memberSubscriptionItem?.region?.toLowerCase()
  const [, handleSwitchRegion] = useSwitchRegion(memberRegion)

  const canRedeemLuxPlusBenefitsAnyRegion =
    isLuxPlusEnabled &&
    memberSubscriptionItem?.status === 'ACTIVE' &&
    memberSubscriptionItem?.tier !== 'free_preview' &&
    !disableMembership

  const isMember = canRedeemLuxPlusBenefitsAnyRegion && !!memberRegion
  const isShoppingInEligibleRegion = currentRegionCodeLower === LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.ELIGIBLE_REGION
  const isMemberInEligibleHomeRegion = isMember && memberRegion === currentRegionCodeLower && isShoppingInEligibleRegion
  const canShowMemberSwitchRegionBanner = isMember && memberRegion === LUX_PLUS_FLIGHT_VOUCHER_CAMPAIGN.ELIGIBLE_REGION && memberRegion !== currentRegionCodeLower && !isSpoofed
  const canShowMemberBanner = isMemberInEligibleHomeRegion || canShowMemberSwitchRegionBanner
  const canShowNonMemberBanner = !isMember && !isSpoofed && isShoppingInEligibleRegion

  const openFlightVoucherModal = useCallback(() => {
    showModal(<LuxPlusFlightVoucherModal />)
  }, [showModal])

  const isLeLivePromotionState = isLeLivePromotionGeoState(geoStateName)

  const showBanner = isCampaignActive && !isDismissed && !isHidden && !isLeLivePromotionState && !isAgentHubEnabled &&
    isEnabledFlightsLuxPlusPricing &&
    (canShowMemberBanner || canShowNonMemberBanner)

  const memberCtaLabel = intl.formatMessage({
    id: 'DpRmlKoEdcJKfAodMVbe',
    defaultMessage: 'View details',
  })
  const switchRegionCtaLabel = intl.formatMessage({
    id: 'FoCS6E_KYWcmIidXjrqi',
    defaultMessage: 'Switch to Home Region',
  })
  const nonMemberCtaLabel = intl.formatMessage({
    id: '75nfhxK0v8dw6Ns6apVY',
    defaultMessage: 'Become a member',
  })
  const ctaLabel = canShowMemberSwitchRegionBanner ? switchRegionCtaLabel : (canShowMemberBanner ? memberCtaLabel : nonMemberCtaLabel)
  const ctaHref = canShowMemberSwitchRegionBanner ? `/${memberRegion}` : undefined
  const ctaOnClick = canShowMemberSwitchRegionBanner ? handleSwitchRegion : (canShowMemberBanner ? openFlightVoucherModal : undefined)

  return useMemo(() => ({
    showBanner,
    onDismiss,
    ctaLabel,
    ctaTo: canShowMemberBanner ? undefined : FLIGHT_VOUCHER_LANDING_PAGE_PATH,
    ctaHref,
    ctaOnClick,
  }), [canShowMemberBanner, ctaHref, ctaLabel, ctaOnClick, onDismiss, showBanner])
}

export default useLuxPlusFlightVoucherBanner
